export type DeviceStatus =
  | "ACTIVE"
  | "INACTIVE"
  | "PENDING"
  | "DECOMMISSIONED";

export type DeviceType =
  | "AC-04"
  | "AC-05"
  | "AC-06"
  | "AC-HUB"
  | "AC-APP"
  | "GSM-62"
  | "TA74-IP"
  | "TA74-GSM"
  | "ANALOG"
  | "DIGITAL"
  | "MOBILE"
  | "OTHER";

export type VoiceConfigStatus = "PENDING" | "SUCCESS" | "FAILED";

export type DeviceWarnings = {
  lowBattery: boolean;
  weakSignal: boolean;
  checkInTimeout: boolean;
};

export type DeviceGps = {
  latitude: number | null;
  longitude: number | null;
  lastSeenAt: string | null;
  batteryPct: number | null;
  cellSignal: number | null;
  speedKmh: number | null;
  direction: number | null;
};

export type VoiceSync = {
  status: VoiceConfigStatus;
  lastError: string | null;
  provisionedAt: string | null;
};

export type DeviceCustomerRef = {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
};

/** Profile bound to a device (primary via customerId or DeviceCustomerBinding). */
export type DeviceBoundCustomer = {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  isPrimary: boolean;
};

export type DeviceUserRef = {
  id: string;
  name: string;
  surname: string | null;
  email: string;
};

/** Core device shape (list + detail). */
export type Device = {
  id: string;
  unitId: string;
  nickname: string | null;
  imei: string | null;
  phoneNumber: string | null;
  type: DeviceType;
  typeOtherDescription: string | null;
  status: DeviceStatus;
  activationDate: string;
  serialNumber: string | null;
  lastServiceDate: string | null;
  nextServiceDate: string | null;
  notes: string | null;
  customerId: string | null;
  customer: DeviceCustomerRef | null;
  boundCustomers?: DeviceBoundCustomer[];
  userId: string | null;
  user: DeviceUserRef | null;
  batteryLevel: number | null;
  signalStrength: number | null;
  lastCheckIn: string | null;
  gps: DeviceGps | null;
  voiceConfigStatus: VoiceConfigStatus;
  voiceProvisionedAt: string | null;
  voiceLastError: string | null;
  voiceLastAttemptAt: string | null;
  voiceRetryCount: number;
  createdAt: string;
  updatedAt: string;
  warnings: DeviceWarnings;
};

/** Detail / mutation responses also include normalized voice block. */
export type DeviceWithVoice = Device & {
  voiceSync: VoiceSync;
  sipPassword?: string;
};

export type DeviceVoiceAsteriskCheck = {
  checkOk: boolean;
  configured: boolean | null;
  registered: boolean | null;
  online: boolean | null;
  sipUsername: string | null;
  passwordMatches: boolean | null;
  error: string | null;
};

export type DeviceVoiceCredentials = {
  deviceId: string;
  unitId: string;
  sipUsername: string;
  sipPassword: string;
};

export type DeviceVoiceCheckResponse = {
  deviceId: string;
  unitId: string;
  sipPassword: string | null;
  voiceSync: VoiceSync;
  dbUpdated: boolean | null;
  asterisk: DeviceVoiceAsteriskCheck;
  /** @deprecated Legacy aggregate flag — prefer asterisk.checkOk and setup status helpers. */
  ok: boolean;
  message?: string | null;
  /** @deprecated Legacy sidecar payload — prefer asterisk fields. */
  checks?: {
    sidecar?: {
      ok?: boolean;
      error?: string | null;
      status?: number | null;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
};

export type CreateDevicePayload = {
  unitId: string;
  type: DeviceType;
  status: DeviceStatus;
  activationDate: string;
  customerId?: string;
  imei?: string;
  phoneNumber?: string;
  serialNumber?: string;
  lastServiceDate?: string;
  nextServiceDate?: string;
  notes?: string;
  nickname: string;
  typeOtherDescription?: string;
  sipPassword?: string;
};

/** Partial body for PUT /api/devices/:id — only sent fields are updated. */
export type UpdateDevicePayload = {
  unitId?: string;
  type?: DeviceType;
  status?: DeviceStatus;
  activationDate?: string;
  customerId?: string | null;
  imei?: string | null;
  phoneNumber?: string | null;
  serialNumber?: string | null;
  lastServiceDate?: string | null;
  nextServiceDate?: string | null;
  notes?: string | null;
  nickname?: string | null;
  typeOtherDescription?: string | null;
  batteryLevel?: number | null;
  signalStrength?: number | null;
  regenerateSipPassword?: boolean;
  sipPassword?: string;
};

/** @deprecated Use Device — kept for gradual migration of list screens. */
export type DeviceAssignedUser = DeviceUserRef;

/** @deprecated Use DeviceCustomerRef */
export type DeviceLinkedCustomer = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/** List row from GET /api/devices (partial fields allowed from API). */
export type DeviceListItem = Partial<Device> & {
  id: string;
  customer?: DeviceLinkedCustomer | DeviceCustomerRef | null;
  user?: DeviceUserRef | null;
  warnings?: Partial<DeviceWarnings> | null;
};

export function toVoiceSync(
  device: Pick<
    Device | DeviceListItem,
    "voiceConfigStatus" | "voiceLastError" | "voiceProvisionedAt"
  > & { voiceSync?: VoiceSync | null }
): VoiceSync {
  if (device.voiceSync) {
    return device.voiceSync;
  }

  return {
    status: device.voiceConfigStatus ?? "PENDING",
    lastError: device.voiceLastError ?? null,
    provisionedAt: device.voiceProvisionedAt ?? null,
  };
}

export function normalizeVoiceConfigStatus(
  value: unknown
): VoiceConfigStatus {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  if (
    normalized === "SUCCESS" ||
    normalized === "FAILED" ||
    normalized === "PENDING"
  ) {
    return normalized;
  }
  return "PENDING";
}

export function isVoiceErrorLikelyNonRetryable(lastError: string | null) {
  if (!lastError) return false;
  const lower = lastError.toLowerCase();
  return (
    lower.includes("503") ||
    lower.includes("pjsip_realtime_enabled") ||
    lower.includes("voice gateway")
  );
}
